
import { ProductSetInfoKeyType } from '@/__generated__/globalTypes'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { validationMixin } from 'vuelidate'
import ProductSetInfoKeyModal from './ProductSetInfoKeyModal.vue'
import debounceMixin from '@/mixins/debounceMixin'
import { UpsertActionType } from '../categories/types'
import ConfirmDialog from '../ConfirmDialog.vue'
import { CreatedInfoKey } from './types'
import {
  DeleteProductSetInfoKeyDocument,
  DeleteProductSetInfoKeyMutation,
  DeleteProductSetInfoKeyMutationVariables
} from './gql/__generated__/deleteProductSetInfoKey.mutation'
import QUERIES from '@/queries/queries'
import { InfoKeyToCategories } from '@/views/categoriesView/CategoriesViewTypes'
import { ProductSetInfoKeyTranslationsFragment } from '@/views/categoriesView/gql/__generated__/CategoriesView.query'

@Component({
  mixins: [validationMixin, debounceMixin],
  components: {
    ProductSetInfoKeyModal,
    ConfirmDialog
  }
})
export default class ProductSetInfoKeyListModal extends Vue {
  @Prop({ required: true, default: [] }) knownProductSetInfoKeys!: ProductSetInfoKeyTranslationsFragment[]
  @Prop({ required: true, default: [] }) infoKeyToCategories!: InfoKeyToCategories[]
  @Prop({ required: false, default: false }) loadingQuery!: boolean

  isLoading = false

  upsertProductSetInfoKeyType: UpsertActionType | null = null
  productSetInfoKeyToUpdate: ProductSetInfoKeyTranslationsFragment | null = null

  page = 1
  search = ''
  sortBy: string | null = null
  sortDesc = false
  usedInProductSetGroupsCount = 0

  showDeleteProductSetInfoKeyDialog = false
  productSetInfoKeyToDelete: ProductSetInfoKeyTranslationsFragment | null = null

  get show() {
    return this.$store.state.manageCharacteristicsDialogs.showManageCharacteristicsDialog
  }

  get lang() {
    return this.$i18n.locale
  }

  get infoKeyTableHeaders() {
    return [
      {
        text: this.$t('application.productSetKeys.list.characteristic').toString(),
        value: 'characteristic',
        width: '45%',
        sortable: true
      },
      {
        text: this.$t('application.productSetKeys.list.usedIn').toString(),
        value: 'usedIn',
        width: '45%',
        sortable: false
      },
      {
        text: '',
        value: 'actions',
        width: '10%',
        align: 'end',
        sortable: false
      }
    ]
  }

  open() {
    this.$store.commit('setShowManageCharacteristicsDialog', true)
  }

  close() {
    this.$store.commit('setShowManageCharacteristicsDialog', false)
  }

  closeAndClear() {
    this.close()
    this.page = 1
    this.search = ''
    this.sortBy = null
    this.sortDesc = false
  }

  getInfoKeyCategories(productSetInfoKeyId: string | null | undefined) {
    return this.infoKeyToCategories.find((x) => x.productSetInfoKeyId === productSetInfoKeyId)?.categoryNames || []
  }

  categoriesText(productSetInfoKey: ProductSetInfoKeyTranslationsFragment) {
    const categories = this.getInfoKeyCategories(productSetInfoKey.id)

    const displayableCategories = categories.slice(0, 3)
    const categoriesDisplayText =
      categories.length - displayableCategories.length > 1
        ? `${categories.length - displayableCategories.length} ${this.$t('application.productSetKeys.list.others')}`
        : `${categories.length - displayableCategories.length} ${this.$t('application.productSetKeys.list.other')}`

    return categories.length > 3
      ? `${displayableCategories.join(', ')} (+${categoriesDisplayText})`
      : categories.join(', ')
  }

  get filteredInfoKeys() {
    let filtered = this.knownProductSetInfoKeys.map((x) => ({
      id: x.id,
      infoLabel: this.lang === 'nl' ? x.infoLabelNL : x.infoLabelEN,
      infoLabelNL: x.infoLabelNL,
      infoLabelEN: x.infoLabelEN,
      isNotEditable: x.isNotEditable,
      key: x.key,
      categories: this.categoriesText(x)
    }))

    if (this.search) {
      const searchLower = this.search.toLowerCase()
      filtered = filtered.filter(
        (x) => x.infoLabelEN.toLowerCase().includes(searchLower) || x.infoLabelNL.toLowerCase().includes(searchLower)
      )
    }

    if (this.sortBy === 'characteristic') {
      filtered = filtered.slice().sort((a, b) => {
        const labelA = (this.lang === 'nl' ? a.infoLabelNL : a.infoLabelEN).toLowerCase()
        const labelB = (this.lang === 'nl' ? b.infoLabelNL : b.infoLabelEN).toLowerCase()
        return this.sortDesc ? labelB.localeCompare(labelA) : labelA.localeCompare(labelB)
      })
    }

    return filtered
  }

  openUpsertInfoKeyModal(productSetInfoKeyId?: string | null) {
    this.productSetInfoKeyToUpdate = this.filteredInfoKeys.find((x) => x.id === productSetInfoKeyId) ?? null
    this.upsertProductSetInfoKeyType = productSetInfoKeyId ? UpsertActionType.UPDATE : UpsertActionType.SAVE
    this.usedInProductSetGroupsCount = this.productSetInfoKeyToUpdate
      ? this.getInfoKeyCategories(productSetInfoKeyId).length
      : 0

    this.close()
    this.$store.commit('setShowUpsertCharacteristicDialog', true)
  }

  get deleteDescription() {
    return this.$t('application.productSetKeys.delete.description')
      .toString()
      .replace('{{COUNT}}', this.usedInProductSetGroupsCount.toString())
  }

  confirmDeleteDialog(productSetInfoKey: ProductSetInfoKeyTranslationsFragment) {
    this.productSetInfoKeyToDelete = productSetInfoKey
    this.usedInProductSetGroupsCount = this.getInfoKeyCategories(productSetInfoKey.id).length

    if (this.usedInProductSetGroupsCount > 0) {
      this.close()
      this.showDeleteProductSetInfoKeyDialog = true
    } else {
      this.deleteProductInfoKey()
    }
  }

  closeDeleteDialog() {
    this.showDeleteProductSetInfoKeyDialog = false
    this.productSetInfoKeyToDelete = null
    this.open()
  }

  async deleteProductInfoKey() {
    if (!this.productSetInfoKeyToDelete) return

    this.isLoading = true
    this.showDeleteProductSetInfoKeyDialog = false
    this.open()

    try {
      await this.$apolloMutate<DeleteProductSetInfoKeyMutation, DeleteProductSetInfoKeyMutationVariables>({
        mutation: DeleteProductSetInfoKeyDocument,
        variables: {
          productSetInfoKeyId: this.productSetInfoKeyToDelete.id
        },
        // Refetch the CategoriesViewQuery to ensure the info key is removed in the list of the category modal and in the info key list modal
        refetchQueries: [QUERIES.CategoriesViewQuery],
        error: 'E4249'
      })
    } finally {
      this.productSetInfoKeyToDelete = null
      this.isLoading = false
    }
  }

  addNewInfoKey(newlyCreatedInfoKey: CreatedInfoKey) {
    if (!newlyCreatedInfoKey?.infoKeyId || !this.knownProductSetInfoKeys) return

    this.knownProductSetInfoKeys.push({
      id: newlyCreatedInfoKey.infoKeyId,
      infoLabel: this.lang === 'nl' ? newlyCreatedInfoKey.labelNL : newlyCreatedInfoKey.labelEN,
      infoLabelNL: newlyCreatedInfoKey.labelNL,
      infoLabelEN: newlyCreatedInfoKey.labelEN,
      isNotEditable: false,
      key: ProductSetInfoKeyType.Other
    })
  }
}
